import { useEffect, useRef, useState } from 'react';
import './styles.scss';
import Modal from 'react-modal';
import Button from 'components/ui/button';
import { PremiacaoIsolada, Combo } from '../../../types/experienceCatalog';
import { getExperienceTypeTitle, stripCatalogPrefix } from 'utils/experience-catalog';
import catalogStar from 'assets/img/experienceCatalog/catalog-star.svg';
import liveloBanner from 'assets/img/livelo-banner.png';
import liveloLogo from 'assets/img/livelo-logo.png';
import { IoIosClose, IoMdInformationCircleOutline } from 'react-icons/io';
import { IoAlertCircleOutline, IoPersonOutline } from 'react-icons/io5';
import { ART_WIDTH_PX, ART_HEIGHT_PX, IMAGE_BAND_MIN_HEIGHT_PX } from '../cardArt';
import { PurchaseOutcome } from '../purchaseOutcome';

Modal.setAppElement('#root');

// A proporção da faixa de arte mora em `../cardArt`: a página de feedback desenha o mesmo
// card, com a mesma arte, e dois lugares com os mesmos números divergem.

// Altura total do card, em pixels.
//
// Era 450 e não cabia. O título é gerado por `getExperienceTypeTitle`, que para o cartão
// monta "Cartão multi experiências no valor de R$ X" — 5 linhas em `text-3xl`, ~187px.
// Com a faixa, o conteúdo passava de 485px e o `overflow-hidden` comia justamente o botão
// de resgate, sem nenhum sinal de que algo tinha sido cortado.
//
// A altura vale para as duas faces do flip, então o verso também ganha o espaço.
const CARD_HEIGHT_PX = 600;

// Tempo pendente a partir do qual o modal avisa que a fila está cheia (`demoraAnormal` do
// design). Aos 60 s a chamada desiste (`PURCHASE_TIMEOUT_MS`) e o modal vai para o resultado.
const SLOW_QUEUE_NOTICE_MS = 45_000;

type FailedOutcome = Exclude<PurchaseOutcome, { kind: 'confirmada' }>;

// Etapas do modal de resgate. Resgate confirmado não tem etapa: a página navega para o feedback.
type RedeemStep = { name: 'confirmacao' } | { name: 'processando' } | { name: 'resultado'; outcome: FailedOutcome };

const NOT_REDEEMED_TITLE = 'O Resgate dessa experiência não foi efetivado.';

// Textos e cor de cada painel de resultado. As classes ficam escritas por inteiro para o
// Tailwind encontrá-las.
function describeFailure(outcome: FailedOutcome) {
  switch (outcome.kind) {
    case 'esgotada':
      return {
        title: NOT_REDEEMED_TITLE,
        showChosenName: true,
        description: 'esgotou a quantidade de vagas. Selecione uma outra experiência disponível no Catálogo.',
        borderClass: 'border-[#f0325b]',
        iconClass: 'text-[#f0325b]',
      };
    case 'recusada':
      return {
        title: NOT_REDEEMED_TITLE,
        showChosenName: false,
        description: outcome.message,
        borderClass: 'border-[#f0325b]',
        iconClass: 'text-[#f0325b]',
      };
    case 'sem_confirmacao':
      return {
        title: 'Não recebemos a confirmação.',
        showChosenName: false,
        description: 'Verifique no catálogo antes de tentar de novo.',
        borderClass: 'border-[#8a6d00]',
        iconClass: 'text-[#8a6d00]',
      };
  }
}

// Aviso amarelo sobre a disputa por vaga, nas etapas de confirmação e de processamento.
function VacancyWarning({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex gap-2.5 rounded-lg border border-[#e8d886] bg-[#fff7d6] px-3.5 py-3 text-left">
      <IoMdInformationCircleOutline aria-hidden="true" size={16} className="mt-0.5 shrink-0 text-[#8a6d00]" />
      <p className="text-[13px] leading-normal text-[#5c4a00]">{children}</p>
    </div>
  );
}

interface ExperienceCardProps {
  item: PremiacaoIsolada | Combo;
  validity: Date;
  type: 'único' | 'combo';
  purchaseExperience: (item: PremiacaoIsolada | Combo) => Promise<PurchaseOutcome>;
  ruleUrl: string;
}

export default function ExperienceSelectCard({ item, validity, type, purchaseExperience, ruleUrl }: ExperienceCardProps) {
  const [flipped, setFlipped] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showLiveloModal, setShowLiveloModal] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [rulePage, setRulePage] = useState(0);
  const [redeemStep, setRedeemStep] = useState<RedeemStep>({ name: 'confirmacao' });
  const [isQueueSlow, setIsQueueSlow] = useState(false);
  const stepTitleRef = useRef<HTMLHeadingElement>(null);

  // O prefixo de catalogação ("(Combo 28) - ") sai aqui, na fonte única do título: o card
  // o usa na frente, no verso, no modal e nos `aria-label`, e todos devem mostrar o mesmo.
  const getTitle = () => stripCatalogPrefix(item.title);
  const getQuantity = () => {
    if (type === 'combo' && 'totalDias' in item) {
      return item.totalDias;
    }
    if (type === 'único' && 'total' in item && item.type !== 'pontos_Livelo') {
      return item.total;
    }
    if (type === 'único' && 'valor' in item) {
      return item.valor.toLocaleString('pt-BR');
    }
    return 1;
  };
  const getDescription = () => {
    if (type === 'combo' && 'description' in item) {
      return item.regrasDeUso.subtitle;
    }
    return getTitle();
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!flipped) {
        setFlipped(true);
      } else {
        setShowModal(true);
      }
    }
    if (event.key === 'Escape' && flipped) {
      setFlipped(false);
    }
  };

  // O que o parágrafo do card mostra.
  //
  // No combo, o complemento calculado TOMA O LUGAR da `description`. Não é linha nova: em 35
  // dos 37 combos do 1S2026 a `description` do item e o `regrasDeUso.subtitle` são a mesma
  // frase, e o card já a mostrava duas vezes — na linha da estrela e no parágrafo. Trocar o
  // parágrafo pelo valor não perde nada (a frase segue na estrela) e não estica o card, que
  // já estourou a altura uma vez e comeu o botão de resgate em silêncio.
  //
  // Sem complemento — combo de ciclo anterior, documento gravado antes desta feature — o
  // parágrafo volta a ser a `description`, e o card fica exatamente como era.
  //
  // O parágrafo do card mostra `description`, que para o combo é a frase calculada pelo
  // motor ("1 dia livre + Cartão premiação no valor de R$ 13.776,91") e para a premiação
  // isolada é a descrição do item.
  //
  // A frase já foi montada aqui no front, e voltou para o motor em 2026-08-25: ela precisa
  // existir no `extra` do resgate, que é lido por consumidores fora desta tela.
  const descriptionText = item.description;

  // Aviso do motor (`Combo.aviso`): só existe quando o card promete algo que este jogador não
  // tem — hoje, o combo de espécie de quem não tem saldo de Pontos Joga Junto. Aparece na
  // frente do card e no modal de confirmação, as duas telas em que ele decide.
  const aviso = 'aviso' in item && item.aviso ? item.aviso : undefined;

  const redeemedCount = Math.max(Number(item.reedem) || 0, 0);
  const hasLimitedStock = item.amount !== -1;
  const remainingStock = hasLimitedStock ? Math.max(item.amount - redeemedCount, 0) : null;
  const isSoldOut = hasLimitedStock && remainingStock === 0;
  const isLiveloPoints = type === 'único' && (item as PremiacaoIsolada).type === 'pontos_Livelo';
  // Item sem arte cadastrada chega com `""`; payload anterior à feature chega sem o campo.
  // Nos dois casos o card volta a ser exatamente o que era antes da faixa existir.
  const hasImage = !!item.image;

  // O nome da escolha nos painéis de processamento e de resultado: o título do card na
  // premiação isolada e, no combo, a frase com o valor, como na linha "Sua escolha:".
  const chosenName =
    type === 'único'
      ? getExperienceTypeTitle((item as PremiacaoIsolada).type, (item as PremiacaoIsolada).valor)
      : descriptionText || getTitle();

  const isProcessing = redeemStep.name === 'processando';

  useEffect(() => {
    if (!isProcessing) {
      setIsQueueSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setIsQueueSlow(true), SLOW_QUEUE_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [isProcessing]);

  // Trocar de painel remove o botão que tinha o foco, e o foco cairia no <body> sem anunciar a
  // etapa nova. O título de cada painel recebe o foco no lugar dele.
  useEffect(() => {
    stepTitleRef.current?.focus();
  }, [redeemStep]);

  const closeRedeemModal = () => {
    setShowModal(false);
    setIsChecked(false);
    setRedeemStep({ name: 'confirmacao' });
  };

  const confirmRedeem = async () => {
    if (isSoldOut) {
      return;
    }
    setRedeemStep({ name: 'processando' });
    const outcome = await purchaseExperience(item);
    // Confirmado, a página já navegou para o feedback e este card saiu da tela.
    if (outcome.kind !== 'confirmada') {
      setRedeemStep({ name: 'resultado', outcome });
    }
  };

  const retryRedeem = () => {
    setIsChecked(false);
    setRedeemStep({ name: 'confirmacao' });
  };

  const failure = redeemStep.name === 'resultado' ? describeFailure(redeemStep.outcome) : undefined;

  // A tarja de tipo fica sobre a imagem quando há imagem, e no topo do conteúdo quando não há.
  // Nos dois casos ela ocupa o mesmo canto — o que muda é sobre o que ela se apoia.
  //
  // Fica à ESQUERDA: a arte dos itens traz o selo da Livelo no canto superior direito, e a
  // tarja caía em cima dele. O canto esquerdo está livre em todas as 31 artes do 1S2026.
  //
  // O combo usa `purple` (#7358c8) e não o rosa #FFB7AB de antes: branco sobre aquele rosa
  // dava 1.66:1, muito abaixo dos 4.5:1 que a WCAG AA pede para texto pequeno. O roxo com
  // branco dá 5.30:1. Escolha do usuário entre as opções medidas, com dois custos aceitos:
  // a borda do card cai para 2.89:1 sobre o fundo (o card "unico" já vivia com 3.19:1), e o
  // roxo fica a 1.10:1 do azul do "unico" — quase a mesma luminosidade, o que os torna
  // parecidos em tela ruim ou para daltonismo. O que impede a confusão é a própria tarja
  // dizer "combo" ou "unico" por escrito: a cor nunca foi o único sinal.
  const typeBadge = (
    <span
      className={`absolute left-4 top-4 ${type === 'combo' ? 'bg-purple' : 'bg-[#3962FF]'} text-white text-xs px-4 py-1 rounded-full font-medium`}
      role="status"
      aria-label={`Tipo de experiência: ${type === 'combo' ? 'Combinação' : 'Única'}`}
    >
      {type}
    </span>
  );
  const liveloModalTitleId = `livelo-modal-title-${item.itemIdPurchase}`;
  const liveloModalDescriptionId = `livelo-modal-description-${item.itemIdPurchase}`;

  return (
    <article
      // O anel do card aparece no TAB, não no clique.
      //
      // Era `focus-within:`, que dispara quando qualquer descendente recebe foco — e o clique
      // de mouse foca o botão. Daí a borda branca ao apertar o card.
      //
      // `:has(:focus-visible)` é a mesma ideia com o critério certo: o navegador só marca
      // `:focus-visible` quando o foco veio do teclado. Onde `:has()` não existir, a regra
      // inteira é descartada e o card fica sem anel — o elemento focado lá dentro continua
      // mostrando o dele, então a navegação por teclado nunca fica sem indicador.
      className="relative w-[300px] transition-all [&:has(:focus-visible)]:ring-2 [&:has(:focus-visible)]:ring-white [&:has(:focus-visible)]:ring-offset-2"
      style={{ perspective: '1000px', height: CARD_HEIGHT_PX }}
      aria-labelledby={`experience-${item.itemIdPurchase}-title`}
      aria-describedby={`experience-${item.itemIdPurchase}-desc`}
    >
      <div
        className={`relative w-full h-full duration-500 ${flipped ? '' : ''}`}
        style={{
          transformStyle: 'preserve-3d',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
        role="region"
        aria-live="polite"
        aria-atomic="true"
      >
        <div
          className={`absolute w-full h-full bg-[#002647] border-2 ${type === 'combo' ? 'border-purple' : 'border-[#3962FF]'} rounded-xl flex flex-col justify-between overflow-hidden`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="flex min-h-0 flex-1 flex-col">
            {/* Fundo da faixa igual ao do card: com `object-contain`, a sobra lateral vira
                parte do card em vez de uma tarja branca colada no topo. */}
            {hasImage && (
              <div
                className="relative w-full shrink-0 bg-[#002647]"
                style={{ aspectRatio: `${ART_WIDTH_PX} / ${ART_HEIGHT_PX}`, minHeight: IMAGE_BAND_MIN_HEIGHT_PX }}
              >
                <img
                  src={item.image}
                  // Decorativa: a arte é ilustração genérica, reaproveitada entre itens, e o
                  // nome da experiência já vem no <h2> logo abaixo. Descrever aqui só faria o
                  // leitor de tela anunciar o mesmo texto duas vezes.
                  alt=""
                  aria-hidden="true"
                  // `contain` e não `cover`: a arte chega em proporções variadas e recortar
                  // significa decepar parte do desenho sem ninguém perceber. Sobra fundo nas
                  // laterais quando a proporção não bate, o que é o defeito menos grave.
                  className="h-full w-full object-contain"
                  loading="lazy"
                />
                {typeBadge}
              </div>
            )}
            {/* O conteúdo rola se estourar, em vez de ser cortado em silêncio pelo
                `overflow-hidden` do card. O botão de resgate fica fora daqui, então ele
                nunca some, por mais longo que seja o título. */}
            <div className="relative min-h-0 flex-1 overflow-y-auto p-4">
              {!hasImage && typeBadge}
              <h2
                id={`experience-${item.itemIdPurchase}-title`}
                className={`text-white font-extrabold text-3xl leading-tight ${hasImage ? 'mt-6' : 'mt-12'} focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all`}
                tabIndex={0}
              >
                {/* {type === 'único' && getQuantity()} */}
                {/* {type === 'único' && <br />} */}
                {type === 'único'
                  ? getExperienceTypeTitle((item as PremiacaoIsolada).type, (item as PremiacaoIsolada).valor)
                  : getTitle()}
              </h2>
              <div className="text-yellow-400 text-2xl mt-4 flex items-center">
                <img src={catalogStar} alt="Ícone de estrela do catálogo" className="w-6 h-6" />
                <p className="text-yellow text-sm ml-2">{item.regrasDeUso?.subtitle && item.regrasDeUso.subtitle}</p>
              </div>
              <p
                id={`experience-${item.itemIdPurchase}-desc`}
                className="text-white mt-4 text-sm font-extralight leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                tabIndex={0}
              >
                {descriptionText}
              </p>
              {aviso && (
                <p
                  role="note"
                  className="flex items-start gap-2 text-yellow mt-3 text-xs leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                  tabIndex={0}
                >
                  <IoMdInformationCircleOutline aria-hidden="true" className="shrink-0 mt-0.5" />
                  <span>{aviso}</span>
                </p>
              )}
              {item.amount > 1 && remainingStock !== null && remainingStock > 0 && (
                <p
                  className="flex items-center gap-2 text-blue-light text-sm mt-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                  tabIndex={0}
                >
                  <IoMdInformationCircleOutline aria-hidden="true" />
                  <span>Vagas Disponíveis: {remainingStock}</span>
                </p>
              )}

              {item?.nextDate && (
                <p
                  className="text-blue-light mt-4 text-sm leading-relaxed focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all"
                  tabIndex={0}
                >
                  {item.nextDate}
                </p>
              )}

              {isLiveloPoints && (
                <button
                  type="button"
                  onClick={event => {
                    event.stopPropagation();
                    setShowLiveloModal(true);
                  }}
                  className="mt-4 flex w-full items-center gap-3 rounded-xl border border-[#FF4FA3] bg-white px-3 py-3 text-left shadow-sm transition-all hover:bg-[#FFF5FA] focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2"
                  aria-haspopup="dialog"
                  aria-controls={liveloModalTitleId}
                  aria-label="Abrir modal com promoções da Livelo"
                >
                  <img src={liveloLogo} alt="Livelo" className="h-10 w-auto shrink-0" />
                  <span className="text-sm font-semibold leading-snug text-[#FF4FA3]">
                    Clique e confira as promoções da Livelo!
                  </span>
                </button>
              )}
            </div>
          </div>
          {isSoldOut && <p className="shrink-0 px-4 pb-3 text-center text-sm font-bold text-yellow">Experiência esgotada</p>}
          <button
            tabIndex={-1}
            onClick={() => setFlipped(true)}
            onKeyDown={handleKeyDown}
            disabled={isSoldOut}
            className={`shrink-0 py-4 font-bold w-full border-t border-dashed border-prussian-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 transition-all ${
              isSoldOut
                ? 'bg-gray-400 text-gray-100 cursor-not-allowed'
                : type === 'combo'
                  ? 'bg-purple hover:bg-[#5F49A9] text-white'
                  : 'bg-[#3962FF] hover:bg-[#2948CC] text-white'
            }`}
          >
            QUERO VIVER ESSA EXPERIÊNCIA
          </button>
        </div>

        {/* Verso do Card */}
        <div
          className="absolute w-full h-full bg-white border-2 border-[#3962FF] rounded-xl flex flex-col overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
          aria-label="Verso do card com regras de uso"
        >
          {/* Cabeçalho fixo.
              Fica fora da área que rola para que o ✕ continue alcançável com a regra
              aberta no meio — antes ele rolava junto e sumia, e o único jeito de voltar
              era rolar até o topo. */}
          <div className="relative shrink-0 px-4 pt-4">
            <button
              onClick={() => setFlipped(false)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setFlipped(false);
                }
              }}
              className="absolute right-4 top-4 text-[#002647] font-bold hover:text-[#FF0000] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all w-8 h-8 flex items-center justify-center rounded-full"
              aria-label="Voltar para frente do card"
            >
              ✕
            </button>
            <h3 className="bg-yellow text-black font-bold inline-block px-2 py-1 mb-2 rounded">Regras de uso:</h3>
            <h4
              className="text-[#002647] font-bold mb-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              {getTitle()}
            </h4>
            {/* <p className="text-[#002647] text-sm mb-4">{item.regrasDeUso?.subtitle}</p> */}
          </div>

          {/* Corpo da regra — a única parte que rola.
              As regras de uso do 1S2026 variam de três linhas a três parágrafos, e o verso
              não tinha nenhuma das travas que a frente já tinha: sem `min-h-0 flex-1
              overflow-y-auto` aqui e sem `shrink-0` no botão, o texto longo empurrava o
              botão de resgate para fora do `overflow-hidden` do card. */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {/* Carrossel de regras de uso */}
            {item.regrasDeUso?.rules2
              ? (() => {
                  const rules = [item.regrasDeUso.rules1, item.regrasDeUso.rules2];
                  return (
                    <div>
                      <div
                        className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
                        dangerouslySetInnerHTML={{ __html: rules[rulePage] }}
                        tabIndex={0}
                        role="region"
                        aria-live="polite"
                        aria-atomic="true"
                        aria-label={`Regras de uso, página ${rulePage + 1} de 2`}
                      />
                      <nav
                        className="flex flex-col items-center justify-center mb-2"
                        aria-label="Navegação das páginas de regras"
                      >
                        <div className="flex items-center gap-2 mb-1" role="tablist">
                          {[0, 1].map(idx => (
                            <button
                              key={idx}
                              type="button"
                              role="tab"
                              aria-selected={rulePage === idx}
                              aria-label={`Página ${idx + 1} das regras`}
                              onClick={() => setRulePage(idx)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setRulePage(idx);
                                }
                              }}
                              className={`carousel-dot focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-1 transition-all${rulePage === idx ? ' active' : ''}`}
                            />
                          ))}
                        </div>
                        <span className="text-[#102535] text-sm font-normal leading-none" role="status" aria-live="polite">
                          {rulePage + 1} de 2
                        </span>
                      </nav>
                    </div>
                  );
                })()
              : item.regrasDeUso?.rules1 && (
                  <div
                    className="text-sm text-[#002647] list-disc pl-5 space-y-1 custom-list-disc-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all"
                    dangerouslySetInnerHTML={{ __html: item.regrasDeUso.rules1 }}
                    tabIndex={0}
                    role="region"
                    aria-label="Regras de uso da experiência"
                  />
                )}
          </div>
          <button
            onClick={() => {
              if (!isSoldOut) {
                setShowModal(true);
              }
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (!isSoldOut) {
                  setShowModal(true);
                }
              }
            }}
            disabled={isSoldOut}
            className={`shrink-0 py-4 font-bold w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#002647] focus-visible:ring-offset-2 transition-all ${
              isSoldOut ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-yellow hover:bg-yellow-400 text-[#002647]'
            }`}
            aria-label={`Confirmar seleção da experiência: ${getTitle()}`}
          >
            {isSoldOut ? 'EXPERIÊNCIA ESGOTADA' : 'CONFIRMO QUE QUERO VIVER ESSA EXPERIÊNCIA'}
          </button>
        </div>
      </div>

      <Modal
        isOpen={showLiveloModal}
        onRequestClose={() => setShowLiveloModal(false)}
        contentLabel="Modal de promoções da Livelo"
        className="relative border-2 border-solid border-electric-blue bg-oxford-blue rounded-lg p-4 md:p-6 max-w-[640px] w-full h-fit outline-none focus-visible:ring-2 focus-visible:ring-yellow focus-visible:ring-offset-2 focus-visible:ring-offset-oxford-blue"
        overlayClassName="z-50 fixed bg-black bg-opacity-40 top-0 left-0 w-screen h-screen p-6 pt-[10vh] flex justify-center overflow-y-auto"
        ariaHideApp={false}
        shouldFocusAfterRender={true}
        shouldReturnFocusAfterClose={true}
        role="dialog"
        aria-modal="true"
        aria-labelledby={liveloModalTitleId}
        aria-describedby={liveloModalDescriptionId}
      >
        <Button
          type="button"
          color="primary"
          className="absolute -right-5 -top-5 rounded-r-full rounded-l-full w-10 h-10 p-0 flex justify-center items-center z-50"
          onClick={() => setShowLiveloModal(false)}
          arialabel="Fechar modal de promoções da Livelo"
        >
          <IoIosClose color="white" size={30} />
        </Button>

        <div className="w-full">
          <h2 id={liveloModalTitleId} className="sr-only">
            Promoções Livelo
          </h2>
          <p id={liveloModalDescriptionId} className="sr-only">
            Confira as promoções exclusivas da Livelo para esta opção de resgate.
          </p>
          <img
            src={liveloBanner}
            alt="Banner com promoções da Livelo para participantes do catálogo de experiências"
            className="w-full h-auto rounded-md"
          />
        </div>
      </Modal>

      <Modal
        isOpen={showModal}
        onRequestClose={closeRedeemModal}
        // Com o pedido no ar, fechar o modal esconderia o resultado: a página não tem outro
        // lugar que o mostre.
        shouldCloseOnEsc={!isProcessing}
        shouldCloseOnOverlayClick={!isProcessing}
        contentLabel="Modal de confirmação da experiência selecionada"
        className={`bg-white border-2 ${failure ? failure.borderClass : 'border-[#3962FF]'} rounded-xl p-6 max-w-2xl w-full mx-4 relative outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2`}
        overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        ariaHideApp={false}
        shouldFocusAfterRender={true}
        shouldReturnFocusAfterClose={true}
        role="dialog"
        // Pela prop `aria`: o react-modal descarta `aria-labelledby` e `aria-describedby` passados
        // direto. Cada etapa desenha o seu `modal-title` e o seu `modal-description`.
        aria={{ labelledby: 'modal-title', describedby: 'modal-description' }}
      >
        {redeemStep.name === 'confirmacao' && (
          <button
            onClick={closeRedeemModal}
            className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 text-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all w-8 h-8 flex items-center justify-center rounded-full"
            aria-label="Fechar modal de confirmação"
          >
            ✕
          </button>
        )}

        {redeemStep.name === 'confirmacao' && (
          <div className="text-center">
            <h2
              id="modal-title"
              ref={stepTitleRef}
              className="text-xl mb-4 text-[#002647] font-bold focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              Atenção!
            </h2>
            <p
              id="modal-description"
              className="mb-6 text-prussian-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              {/* Este aviso dizia que a escolha era definitiva e que não seria possível trocar.
                Virou mentira quando a feature `desfazer-resgate-catalogo` passou a permitir
                desfazer — ilimitadamente, até `periodo_catalogo__c.endDate` (decisão D-04) —
                e ninguém voltou aqui para atualizar o texto. A página de feedback já dizia o
                contrário ("Você pode desfazer sua escolha até …"), então as duas telas se
                contradiziam, e a que mentia era justamente a do momento da decisão.

                A data vem de `validity`, que é o mesmo `deadLine` do ciclo que a página de
                feedback usa e que o contador logo abaixo já mostra — uma fonte só. */}
              Você pode trocar sua escolha até <strong>{validity.toLocaleDateString('pt-BR')}</strong>, na página da sua
              experiência.
              <strong> Após essa data, a escolha se torna definitiva.</strong>
            </p>

            <div
              className="bg-gray-100 rounded-lg p-4 mb-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 transition-all"
              tabIndex={0}
            >
              <h3 className="font-bold text-[#002647] mb-1">Sua escolha:</h3>
              <p className="text-base text-[#002647] font-bold">
                {/* No combo, a linha de cima é a FRASE COM O VALOR, e não o título.
                  O título ("1 dia livre + Cartão premiação") é a mesma frase sem o valor, e o
                  modal mostrava as duas — a mesma coisa, dita duas vezes, com a versão útil
                  embaixo. Fica só a que informa, e no lugar de mais destaque: esta é a tela
                  em que a escolha vira definitiva.

                  Recuo para o título quando não há frase: combo de ciclo anterior, cujo
                  documento foi gravado antes de o motor voltar a calculá-la. */}
                {type === 'único' && <>{getQuantity()} </>}
                <strong>{type === 'combo' ? descriptionText || getTitle() : getTitle()}</strong>
                {type === 'combo' && <span className="block text-gray-600">{getDescription()}</span>}
                {aviso && (
                  <span role="note" className="block mt-2 text-sm text-[#002647] font-semibold">
                    {aviso}
                  </span>
                )}
              </p>
            </div>
            <fieldset className="flex items-start gap-3 mb-6 text-left">
              <legend className="sr-only">Confirmação de ciência do regulamento</legend>
              <input
                type="checkbox"
                id={`regulation-checkbox-${item.itemIdPurchase}`}
                checked={isChecked}
                onChange={e => setIsChecked(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#3962FF] bg-gray-100 border-gray-300 rounded focus:ring-[#3962FF] focus:ring-2 focus:ring-offset-1"
                aria-describedby={`regulation-desc-${item.itemIdPurchase}`}
              />
              <label
                htmlFor={`regulation-checkbox-${item.itemIdPurchase}`}
                className="text-sm text-gray-700 cursor-pointer hover:text-gray-900 transition-colors"
              >
                <span id={`regulation-desc-${item.itemIdPurchase}`}>
                  Declaro que estou ciente do{' '}
                  <a
                    href={ruleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline hover:text-[#2948CC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2948CC] focus-visible:ring-offset-1 transition-colors font-medium"
                    onClick={e => e.stopPropagation()}
                    aria-label="Abrir regulamento em nova aba"
                  >
                    Regulamento
                  </a>{' '}
                  e das diretrizes do programa
                </span>
              </label>
            </fieldset>
            {/* O contador "Prazo para escolha" saiu daqui: o design o removeu, e a página já
              mostra o mesmo prazo acima da vitrine. No lugar dele, o aviso de que confirmar
              não garante a vaga. */}
            <div className="mb-6">
              <VacancyWarning>
                As vagas são limitadas. Confirmar envia sua solicitação para processamento e{' '}
                <strong>não garante o resgate</strong>.
              </VacancyWarning>
            </div>

            <div className="flex gap-3" role="group" aria-label="Ações de confirmação">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 uppercase bg-gray-200 text-gray-700 py-3 px-4 rounded-lg font-semibold hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2 transition-colors"
                aria-label="Voltar sem confirmar a experiência"
              >
                Voltar
              </button>
              <button
                onClick={confirmRedeem}
                disabled={!isChecked || isSoldOut}
                className={`flex-1 py-3 px-4 uppercase rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                  isChecked && !isSoldOut
                    ? 'bg-[#3962FF] text-white hover:bg-[#2948CC] focus-visible:ring-[#3962FF]'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed focus-visible:ring-gray-400'
                }`}
                aria-label={
                  isSoldOut
                    ? 'Experiência esgotada'
                    : !isChecked
                      ? 'Marque a confirmação do regulamento para continuar'
                      : `Confirmar experiência: ${getTitle()}`
                }
                aria-describedby={!isChecked ? `regulation-desc-${item.itemIdPurchase}` : undefined}
              >
                {isSoldOut ? 'Experiência esgotada' : 'quero viver essa experiência'}
              </button>
            </div>
          </div>
        )}

        {isProcessing && (
          <div className="mx-auto flex max-w-[440px] flex-col gap-4 py-1 text-left">
            <h2 id="modal-title" ref={stepTitleRef} tabIndex={-1} className="text-xl font-bold text-[#002647] focus:outline-none">
              Solicitação em processamento
            </h2>
            <p className="font-bold text-[#002647]">{chosenName}</p>
            {/* Região viva desde a abertura do painel: é ela que anuncia o aviso de fila quando
                ele toma o lugar do aviso de vagas. */}
            <div id="modal-description" role="status" className="flex flex-col gap-4">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="h-6 w-6 shrink-0 rounded-full border-2 border-[#dbe3f5] border-t-[#3962FF] motion-safe:animate-spin"
                />
                <span className="font-bold text-[#002647]">Estamos processando sua solicitação</span>
              </div>
              <div aria-hidden="true" className="relative h-1.5 w-full overflow-hidden rounded-full bg-[#e9edf6]">
                <div className="redeem-progress-indeterminate absolute top-0 h-full w-2/5 rounded-full bg-[#3962FF]" />
              </div>
              {isQueueSlow ? (
                <div className="flex gap-2.5 rounded-lg border border-[#c7d3ee] bg-[#eef2fb] px-3.5 py-3">
                  <IoPersonOutline aria-hidden="true" size={17} className="mt-0.5 shrink-0 text-[#2948CC]" />
                  <p className="text-[13px] leading-normal text-[#2c3a55]">
                    <strong>Muita gente escolhendo agora.</strong> Sua solicitação está na fila e pode levar mais alguns minutos.
                    Pode continuar aqui: avisamos assim que terminar.
                  </p>
                </div>
              ) : (
                <VacancyWarning>
                  As vagas são limitadas. <strong>Só ao final do processamento saberemos se você conseguiu o resgate.</strong>
                </VacancyWarning>
              )}
            </div>
          </div>
        )}

        {redeemStep.name === 'resultado' && failure && (
          <div className="flex flex-col items-center gap-4 py-1 text-center">
            <IoAlertCircleOutline aria-hidden="true" size={42} className={failure.iconClass} />
            <h2
              id="modal-title"
              ref={stepTitleRef}
              tabIndex={-1}
              className="max-w-[460px] text-xl font-bold text-[#002647] focus:outline-none"
            >
              {failure.title}
            </h2>
            <p id="modal-description" className="max-w-[460px] text-left text-[15px] leading-normal text-[#002647]">
              {failure.showChosenName && (
                <>
                  <strong>{chosenName}</strong>{' '}
                </>
              )}
              {failure.description}
            </p>
            <div className="mt-1 flex flex-wrap justify-center gap-3">
              {redeemStep.outcome.kind === 'sem_confirmacao' && (
                <button
                  type="button"
                  onClick={retryRedeem}
                  className="rounded-lg bg-gray-200 px-6 py-3 font-semibold uppercase text-gray-700 transition-colors hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-offset-2"
                >
                  Tentar novamente
                </button>
              )}
              <button
                type="button"
                onClick={closeRedeemModal}
                className="rounded-lg bg-[#3962FF] px-6 py-3 font-semibold uppercase text-white transition-colors hover:bg-[#2948CC] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3962FF] focus-visible:ring-offset-2"
              >
                Ver catálogo
              </button>
            </div>
          </div>
        )}
      </Modal>
    </article>
  );
}
